// Helper utilities for resolving the Ruckus R1 "Controller Name" for a keypool.
//
// R1 keypools (AuthenticationMechanism === "WPA3") are synced from a Ruckus R1
// controller. Each keypool carries a "Scope" value that is the Ruckus
// identifier (DPSK pool identifier). The list of controllers returned by
// /operatorconsole/config/listRuckusR1Controllers includes, for each
// controller, its "ControllerName" and an "AvailableDPSKPools" array. We map a
// keypool to its controller by matching the keypool's Scope against the id of
// one of the controller's available DPSK pools.
//
// The exact shape of an AvailableDPSKPools item is not otherwise consumed in
// the frontend, so this helper matches defensively against several likely
// identifier field names as well as plain-string pool entries.

const POOL_ID_FIELDS = [
    "id",
    "ID",
    "Id",
    "Scope",
    "scope",
    "PoolID",
    "poolId",
    "poolID",
    "DPSKPoolID",
    "dpskPoolId",
    "dpskPoolID",
    "DPSKServiceID",
    "ServiceID",
    "serviceId",
];

// Returns true when the keypool is a Ruckus R1 (WPA3) keypool.
export const isR1Keypool = (keypool) => {
    if (!keypool) return false;
    return keypool["AuthenticationMechanism"] === "WPA3";
};

// Extracts a comparable identifier from a single DPSK pool entry.
const getPoolIdentifiers = (pool) => {
    if (pool === undefined || pool === null) return [];
    if (typeof pool === "string" || typeof pool === "number") {
        return [String(pool)];
    }
    if (typeof pool === "object") {
        return POOL_ID_FIELDS.map((field) => pool[field]).filter(
            (value) => value !== undefined && value !== null && value !== ""
        ).map((value) => String(value));
    }
    return [];
};

// Given the keypool's Scope and the list of R1 controllers, returns the name of
// the controller whose available DPSK pools include that scope. Returns "" when
// no match is found.
export const getR1ControllerNameForScope = (scope, ruckusR1Controllers) => {
    if (!scope || !Array.isArray(ruckusR1Controllers)) return "";

    const targetScope = String(scope);

    for (const controller of ruckusR1Controllers) {
        if (!controller) continue;
        const pools = controller["AvailableDPSKPools"];
        if (!Array.isArray(pools)) continue;

        for (const pool of pools) {
            const identifiers = getPoolIdentifiers(pool);
            if (identifiers.includes(targetScope)) {
                return controller["ControllerName"] || "";
            }
        }
    }

    return "";
};

// Convenience wrapper that resolves the controller name for a full keypool
// object. Returns "" for non-R1 keypools or when no controller matches.
export const getR1ControllerNameForKeypool = (keypool, ruckusR1Controllers) => {
    if (!isR1Keypool(keypool)) return "";
    return getR1ControllerNameForScope(
        keypool["Scope"],
        ruckusR1Controllers
    );
};

export default getR1ControllerNameForKeypool;
